import { CellSelectionOptions, ColDef, GridOptions, RowNumbersOptions } from "ag-grid-community";
import { useMemo } from "react";

import CellRenderer from "../CellRenderer";
import { GRID_DIMENSIONS, GRID_STYLE_VARS } from "../constants";
import LoadingOverlay from "../LoadingOverlay";
import NoRowsOverlay from "../NoRowsOverlay";
import { ClipboardConfig, DisplayConfig, EditingConfig, SelectionConfig } from "../types";
import { pickDefined } from "../utils";

interface UseGridDefaultsProps {
  editing?: EditingConfig;
  selection?: SelectionConfig;
  clipboard?: ClipboardConfig;
  display?: DisplayConfig;
  emptyStateMessage: string;
  rowNumbers?: boolean | RowNumbersOptions;
  gridOptions?: Partial<GridOptions>;
}

/**
 * Derives the `defaultColDef`, `gridOptions`, and `containerStyle` from
 * the component's grouped config props.
 *
 * Every returned value is memoised so ag-grid receives stable references
 * across renders (per the React best-practices guide).
 */
export const useTableDefaults = ({
  editing,
  selection,
  clipboard,
  display,
  emptyStateMessage,
  rowNumbers,
  gridOptions: customGridOptions,
}: UseGridDefaultsProps) => {
  const editable = editing?.enabled ?? true;
  const enableSorting = display?.enableSorting ?? true;
  const enableFiltering = display?.enableFiltering ?? false;
  const enableResizing = display?.enableResizing ?? true;
  const enableCellChangeFlash = display?.enableCellChangeFlash ?? true;

  const defaultColDef = useMemo<ColDef>(
    () => ({
      editable,
      sortable: enableSorting,
      resizable: enableResizing,
      filter: enableFiltering,
      enableCellChangeFlash,
      minWidth: GRID_DIMENSIONS.defaultMinColumnWidth,
      cellRenderer: CellRenderer,
    }),
    [editable, enableSorting, enableResizing, enableFiltering, enableCellChangeFlash],
  );

  const resolvedCellSelection = useMemo<CellSelectionOptions | undefined>(() => {
    const cellSelection = selection?.cellSelection ?? true;

    if (cellSelection === false) {
      return undefined;
    }

    if (cellSelection === true) {
      return {
        handle: { mode: "fill" as const },
        enableHeaderHighlight: true,
      };
    }

    return cellSelection;
  }, [selection?.cellSelection]);

  const gridOptions = useMemo<GridOptions>(() => {
    const singleClickEdit = editing?.singleClick ?? false;
    const stopEditingWhenCellsLoseFocus = editing?.stopOnBlur ?? true;
    const readOnlyEdit = editing?.readOnly ?? false;
    const invalidEditValueMode = editing?.invalidValueMode ?? "revert";
    const enableCellTextSelection = selection?.enableTextSelection ?? false;

    return {
      rowHeight: GRID_DIMENSIONS.defaultRowHeight,
      headerHeight: GRID_DIMENSIONS.defaultHeaderHeight,

      // Editing
      singleClickEdit,
      stopEditingWhenCellsLoseFocus,
      readOnlyEdit,
      invalidEditValueMode,

      // Selection & interaction
      cellSelection: resolvedCellSelection,
      enableCellTextSelection,
      suppressRowHoverHighlight: false,

      // Clipboard - use pickDefined to eliminate null-check spreading
      ...pickDefined({
        suppressClipboardPaste: clipboard?.suppressPaste,
        suppressCutToClipboard: clipboard?.suppressCut,
        copyHeadersToClipboard: clipboard?.copyHeaders,
        clipboardDelimiter: clipboard?.delimiter,
        processCellForClipboard: clipboard?.processCellForClipboard,
        processCellFromClipboard: clipboard?.processCellFromClipboard,
        processHeaderForClipboard: clipboard?.processHeaderForClipboard,
        processGroupHeaderForClipboard: clipboard?.processGroupHeaderForClipboard,
        processDataFromClipboard: clipboard?.processDataFromClipboard,
      }),

      // Layout
      domLayout: "normal" as const,

      // Performance
      suppressAnimationFrame: false,
      suppressScrollOnNewData: true,
      rowNumbers,

      // Overlays - stable references; pass config via params
      loadingOverlayComponent: LoadingOverlay,
      noRowsOverlayComponent: NoRowsOverlay,
      noRowsOverlayComponentParams: { message: emptyStateMessage },

      // Consumer escape-hatch – merged last so it can override anything above
      ...customGridOptions,
    };
  }, [
    editing?.singleClick,
    editing?.stopOnBlur,
    editing?.readOnly,
    editing?.invalidValueMode,
    resolvedCellSelection,
    selection?.enableTextSelection,
    clipboard?.suppressPaste,
    clipboard?.suppressCut,
    clipboard?.copyHeaders,
    clipboard?.delimiter,
    clipboard?.processCellForClipboard,
    clipboard?.processCellFromClipboard,
    clipboard?.processHeaderForClipboard,
    clipboard?.processGroupHeaderForClipboard,
    clipboard?.processDataFromClipboard,
    emptyStateMessage,
    rowNumbers,
    customGridOptions,
  ]);

  const containerStyle = useMemo(
    () =>
      ({
        "--ag-header-background-color": GRID_STYLE_VARS.headerBackgroundColor,
        "--ag-header-foreground-color": GRID_STYLE_VARS.headerForegroundColor,
        "--ag-row-hover-color": GRID_STYLE_VARS.rowHoverColor,
        "--ag-border-color": GRID_STYLE_VARS.borderColor,
        "--ag-header-height": `${GRID_DIMENSIONS.defaultHeaderHeight}px`,
        "--ag-cell-horizontal-padding": GRID_STYLE_VARS.cellHorizontalPadding,
        "--ag-font-family": GRID_STYLE_VARS.fontFamily,
        "--ag-font-size": GRID_STYLE_VARS.fontSize,
        "--ag-line-height": GRID_STYLE_VARS.lineHeight,
      }) as React.CSSProperties,
    [],
  );

  return { defaultColDef, gridOptions, containerStyle };
};
